import { BNPL_MAX_TRANSACTION_UNIT_AMOUNT } from 'constants/bnpl';
import { BNPL_MIN_TRANSACTION_UNIT_AMOUNT } from 'constants/bnpl';
import n from 'numeral';

import { TService, TServiceOption } from 'types/service';

import { formatCurrency } from './helpers';
import { convertDecimalToSubunits } from './payments/money';

/**
 * Get a deposit text from a service, an option, or options
 * @param serviceOrOptionOrOptions either a service, an option, or options to get deposit text from
 * @param newClientOnlyEnabled feature flag
 * @returns deposit text string or empty string when is not needed
 */
export const getDepositText = (
  serviceOrOptionOrOptions: TService | TServiceOption | TServiceOption[],
) => {
  if (Array.isArray(serviceOrOptionOrOptions)) return '';

  const deposit = serviceOrOptionOrOptions?.deposit_amount || 0;
  const depositText = `${formatCurrency(deposit)} deposit required`;
  const newClientText = serviceOrOptionOrOptions?.deposit_for_new_clients_only
    ? ' for new clients'
    : '';

  return deposit ? `${depositText}${newClientText}` : '';
};

/**
 * Get a duration text from a service, an option, or options
 * @param serviceOrOptionOrOptions either a service, an option, or options to get duration text from
 * @returns duration text string
 */
export const getDurationText = (
  serviceOrOptionOrOptions: TService | TServiceOption | TServiceOption[],
) => {
  const shouldDisplayMinDuration = Array.isArray(serviceOrOptionOrOptions);
  let duration = 0;

  if (shouldDisplayMinDuration) {
    const options = serviceOrOptionOrOptions;

    duration = Math.min(
      ...options.map(
        (o) =>
          (o.start_duration || 0) +
          (o.processing_duration || 0) +
          (o.end_duration || 0),
      ),
    );
  } else {
    const serviceOrOption = serviceOrOptionOrOptions;

    duration =
      (serviceOrOption?.start_duration || 0) +
      (serviceOrOption?.processing_duration || 0) +
      (serviceOrOption?.end_duration || 0);
  }

  return `${duration}${shouldDisplayMinDuration ? '+' : ''} min`;
};

/**
 * Get a price text from a service, an option, or options
 * @param serviceOrOptionOrOptions either a service, an option, or options to get price text from
 * @returns price text string
 */
export const getDisplayedPrice = (
  serviceOrOptionOrOptions: TService | TServiceOption | TServiceOption[],
) => {
  const shouldDisplayMinPrice = Array.isArray(serviceOrOptionOrOptions);

  if (shouldDisplayMinPrice) {
    const options = serviceOrOptionOrOptions;
    const allPriceHidden = options.every((o) => o.price_hidden);

    if (allPriceHidden) {
      return 'Price Varies';
    }

    const optionsWithPrice = options.filter((o) => !o.price_hidden);
    const minPrice = Math.min(...optionsWithPrice.map((o) => Number(o.price)));

    return `$${minPrice}+`;
  }

  const serviceOrOption = serviceOrOptionOrOptions;

  if (serviceOrOption.price_hidden) {
    return 'Price Varies';
  }

  if (serviceOrOption.price_varies && serviceOrOption.price) {
    return `$${Number(serviceOrOption.price)}+`;
  }

  return n(serviceOrOption.price).format('$0,0');
};

export const getServiceOrOptionBnplUnitPrice = (
  serviceOrOption: TServiceOption | TService,
) => {
  const unitPrice =
    convertDecimalToSubunits(Number(serviceOrOption.price)) ?? 0;
  const withinRange =
    unitPrice >= BNPL_MIN_TRANSACTION_UNIT_AMOUNT &&
    unitPrice <= BNPL_MAX_TRANSACTION_UNIT_AMOUNT;

  if (serviceOrOption.price_hidden || !withinRange) {
    return undefined;
  }

  return unitPrice;
};

export const getBnplUnitPrice = (
  serviceOrOptionOrOptions: TService | TServiceOption | TServiceOption[],
) => {
  const shouldDisplayMinPrice = Array.isArray(serviceOrOptionOrOptions);

  if (shouldDisplayMinPrice) {
    const options = serviceOrOptionOrOptions;
    const allPriceHidden = options.every((o) => o.price_hidden);

    if (allPriceHidden) {
      return undefined;
    }

    const optionsWithPrice = options.filter((o) => !o.price_hidden);
    const minPrice = Math.min(...optionsWithPrice.map((o) => Number(o.price)));
    const unitPrice = convertDecimalToSubunits(minPrice);
    const withinRange =
      unitPrice >= BNPL_MIN_TRANSACTION_UNIT_AMOUNT &&
      unitPrice <= BNPL_MAX_TRANSACTION_UNIT_AMOUNT;

    return withinRange ? unitPrice : undefined;
  }

  return getServiceOrOptionBnplUnitPrice(serviceOrOptionOrOptions);
};

/**
 * Get an option text from an option, or options
 * @param [optionOrOptions] either an option, or options to get price text from
 * @returns price text string
 */
export const getOptionText = (
  optionOrOptions?: TServiceOption | TServiceOption[],
) => {
  if (!optionOrOptions) return '';

  if (Array.isArray(optionOrOptions)) {
    const count = optionOrOptions.length;

    return `${count} option${count > 1 ? 's' : ''}`;
  }

  return optionOrOptions.name;
};

/**
 * Converts service GUIDs to service tokens by looking up both services and service options
 * @param services - Array of service objects with guid, token, and optional options array
 * @param serviceOrOptionsGuids - Array of service/option GUIDs to get tokens for
 * @returns Array of service tokens
 */
export function getServiceTokensFromGuids(
  services: TService[],
  serviceOrOptionsGuids: string[],
): string[] {
  const serviceTokenMap = new Map<string, string>();

  services?.forEach((service) => {
    if (service.token) {
      serviceTokenMap.set(service.guid, service.token);
    }

    service.options?.forEach((option) => {
      if (option.token) {
        serviceTokenMap.set(option.guid, option.token);
      }
    });
  });

  return (serviceOrOptionsGuids || [])
    .map((serviceOrOptionGuid) => serviceTokenMap.get(serviceOrOptionGuid))
    .filter((token): token is string => Boolean(token));
}

/**
 * Converts service tokens to service GUIDs by looking up both services and service options
 * @param services - Array of service objects with guid, token, and optional options array
 * @param serviceOrOptionsTokens - Array of service/option tokens to get GUIDs for
 * @returns Array of service GUIDs
 */
export function getGuidsFromTokens(
  services: TService[],
  serviceOrOptionsTokens: string[],
): string[] {
  const tokenToGuidMap = new Map<string, string>();

  services?.forEach((service) => {
    if (service.token) {
      tokenToGuidMap.set(service.token, service.guid);
    }

    service.options?.forEach((option) => {
      if (option.token) {
        tokenToGuidMap.set(option.token, option.guid);
      }
    });
  });

  return (serviceOrOptionsTokens || [])
    .map((token) => tokenToGuidMap.get(token))
    .filter((guid): guid is string => Boolean(guid));
}
